import type { AutoCompleteResult } from '@awazevr/design-system.components.autocomplete'
import {
  DataLayerEventType,
  DataLayerFactory,
  type TmsContextType,
} from '@awazevr/mfe-common.contexts.tms'

import { dataLayerErrorHandler } from '@/utils/dataLayerErrorHandler'

import type { PlacesSuggestions } from './SearchForm.types'

type OriginalAutoSuggestSearch = (
  searchTerm: string,
  accommodationType: string,
  propertiesOnly: boolean,
) => Promise<AutoCompleteResult[]>

const fireAutoSuggestTrackingEvents = ({
  searchTerm,
  accommodationType,
  destinations,
  properties,
  noResult,
  tms,
}: {
  searchTerm: string
  accommodationType?: string
  destinations: string[]
  properties: string[]
  noResult: boolean
  tms?: TmsContextType
}): void => {
  if (!tms) {
    return
  }

  if (noResult) {
    tms.updateDataLayer('link', 'auto_suggest_noresult', {
      search_term: searchTerm,
    })
    return
  }

  new DataLayerFactory()
    .getEvent(DataLayerEventType.AutoSuggestImpression)
    .update(
      tms,
      {
        searchTerm,
        accommodationType,
        destinations,
        properties,
      },
      dataLayerErrorHandler,
    )
}

export const combineAutoSuggestSearch = (
  getSuggestions: PlacesSuggestions,
  originalAutoSuggestSearch: OriginalAutoSuggestSearch,
  tms?: TmsContextType,
): ((
  searchTerm: string | null,
  accommodationType?: string,
) => Promise<AutoCompleteResult[]>) => {
  return async (
    searchTerm: string | null,
    accommodationType?: string,
  ): Promise<AutoCompleteResult[]> => {
    if (!searchTerm) {
      return []
    }

    // Skip Places lookup entirely for boat-holidays — only use location lookup
    const forceLocationLookupIfBoats = accommodationType === 'boat-holidays'

    if (forceLocationLookupIfBoats) {
      const originalResult = await originalAutoSuggestSearch(
        searchTerm,
        accommodationType,
        false,
      )

      fireAutoSuggestTrackingEvents({
        searchTerm,
        accommodationType,
        destinations: originalResult
          .filter((result) => !result.property)
          .map(({ name }) => name),
        properties: originalResult
          .filter((result) => result.property)
          .map(({ name }) => name),
        noResult: originalResult.length === 0,
        tms,
      })

      return originalResult
    }

    const [placesResult, originalResult] = await Promise.all([
      getSuggestions(searchTerm),

      originalAutoSuggestSearch(searchTerm, accommodationType as string, true),
    ])

    const propertyResults = originalResult.filter((r) => r.property)

    fireAutoSuggestTrackingEvents({
      searchTerm,
      accommodationType,
      destinations: placesResult.map(({ name }) => name),
      properties: propertyResults.map(({ name }) => name),
      noResult: placesResult.length === 0 && propertyResults.length === 0,
      tms,
    })

    return [...placesResult, ...propertyResults]
  }
}
